/**
 * Post Authentication Requests Saga
 */

/* tslint:disable:space-before-function-paren */

import { pushNotification } from '../actions/notifications';
import {
  clearPostAuthenticationRequests,
  consumePostAuthenticationRequests,
  IPostAuthenticationCreateProjectRequestActionPayload,
  IPostAuthenticationDownloadTrackRequestActionPayload,
  IPostAuthenticationJoinProjectRequestActionPayload,
  IPostAuthenticationRequest,
} from '../actions/postAuthenticationRequests';
import * as projectActions from '../actions/projects';
import { showDownloadAgreementModal } from '../actions/ui';
import { addTrackToProject, createProject } from '../helpers/projects';
import { getViewerId } from '../selectors/user';

import { Actions as farceActions } from 'farce';
import { LocationDescriptor } from 'found';
import { chain } from 'lodash';
import { DateTime } from 'luxon';
import { effects as fx } from 'redux-saga';

import { RegistrationPagePath } from '../../components/pages/account/Registration';
import { BrowsePagePath } from '../../components/pages/browse/Browse';

export function* init() {
  yield fx.all([
    fx.call(watchForConsume),
  ]);
}

const getPostAuthenticationRequests = ({ postAuthenticationRequests }: IStoreState) => postAuthenticationRequests;
const getUserProfile = (state: IStoreState) => state.user.profile;

function* watchForConsume() {
  while (true) {
    yield fx.take(consumePostAuthenticationRequests);
    const dateIsValid = (dateString: string) => DateTime.fromISO(dateString).plus({ hours: 1 }) >= DateTime.local();
    const requests: IPostAuthenticationRequest[] = yield fx.select(getPostAuthenticationRequests);
    const userProfile: GQL.IProfile | undefined = yield fx.select(getUserProfile);
    const validRequests =
      chain(requests)
        .filter(({ requestedAt }) => dateIsValid(requestedAt))
        .uniqBy(getUniqueRequestIdentifier)
        .value();

    let pageToRedirect: LocationDescriptor = BrowsePagePath;

    if (validRequests.length) {
      for (const currentRequest of validRequests) {
        const { payload, page, requestType } = currentRequest;

        if (page) {
          pageToRedirect = page;
        }

        switch (requestType) {
          case 'createProject':
            yield fx.call(onCreateProject, payload as IPostAuthenticationCreateProjectRequestActionPayload);
            break;
          case 'downloadTrack':
            const { path: filePath, track } = payload as IPostAuthenticationDownloadTrackRequestActionPayload;
            yield fx.put(showDownloadAgreementModal({ filePath, track }));
            break;
          case 'joinProject':
            const { projectOpaqueId } = payload as IPostAuthenticationJoinProjectRequestActionPayload;
            yield fx.put(projectActions.requestJoinProject({
              input: {
                opaque_id: projectOpaqueId,
              },
              promise: {
                resolve: () => null,
                reject: () => null,
              },
            }));
            break;
        }
      }
    }

    yield fx.put(clearPostAuthenticationRequests());
    yield fx.call(redirect, userProfile && userProfile.is_new_user ?
      {
        pathname: RegistrationPagePath,
        search: `?goto=${pageToRedirect}`,
      } : pageToRedirect);
  }
}

function* onCreateProject(payload: IPostAuthenticationCreateProjectRequestActionPayload) {
  yield fx.call(onCreateProjectAndAddTrack, payload);
}

function* redirect(path: string | Partial<IRouterLocation>) {
  yield fx.put(farceActions.push(path));
}

function getUniqueRequestIdentifier({ payload, requestType }: IPostAuthenticationRequest) {
  return `${requestType}:${JSON.stringify(payload)}`;
}

function* onCreateProjectAndAddTrack({
  projectName,
  trackId,
  trackVersion,
  searchState,
}: IPostAuthenticationCreateProjectRequestActionPayload) {
  const input = { name: projectName };
  const viewerId = yield fx.select(getViewerId);

  try {
    const { response, errors } = yield fx.call(createProject, { input }, viewerId);
    if (errors) {
      yield fx.put(pushNotification({
        autoDismiss: false,
        message: 'An error occurred while creating playlist. Please try again.',
        state: 'error',
      }));
    } else {
      const projectId = response!.createProject!.new_project_edge!.node!.id;
      try {
        const licenseParams = searchState.license_params;
        yield fx.call(addTrackToProject, { licenseParams, projectId, trackId, trackVersion });
        yield fx.put(pushNotification({
          message: 'A track was saved to playlist successfully!',
          state: 'success',
        }));
      } catch (errors) {
        yield fx.put(pushNotification({
          autoDismiss: false,
          message: 'An error occurred while saving this track to a new playlist. Please try again.',
          state: 'error',
        }));
      }
    }
  } catch (error) {
    yield fx.put(pushNotification({
      autoDismiss: false,
      message: 'An error occurred while creating playlist. Please try again.',
      state: 'error',
    }));
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/sagas/postAuthenticationRequests.ts